import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../quotation/Quotation.scss";
import "react-datepicker/dist/react-datepicker.css";
import FetchData, { PaginationFetchData } from "../../providers/dataProvider";
import QuotationGrid from "../../components/quotation/QuotationGrid";
import QuotationToolbar from "../../components/quotation/QuotationToolbar";
import { useRolePermission } from "../../hooks/useRolePermission";
import QuotationGeneralView from "./QuotationGeneralView";

const createFilterRow = (field = "project") => ({
  id: `${field}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  field,
  value: null,
  fromDate: "",
  toDate: "",
});

const Quotation = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [filterRows, setFilterRows] = useState([createFilterRow()]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const userId = localStorage.getItem("authidentity")

  const [filterData, setFilterData] = useState({
    PageNumber: 1,
    PageSize: 25,
    CountryName: "",
    ProjectName: "",
    DistributorName: "",
    CustomerName: "",
    QuoteFromDate: "",
    QuoteToDate: "",
  });
  const [filterDataQuote, setFilterDataQuote] = useState({
    PageNumber: 1,
    PageSize: 25,
    SearchTerm: "",
    userId: userId,
    CountryName: "",
    ProjectName: "",
    DistributorName: "",
    CustomerName: "",
    QuoteFromDate: "",
    QuoteToDate: "",
  });

  const [viewType, setViewType] = useState("project");

  const { data: quotationSummary, loading: summaryLoading } =
    PaginationFetchData(
      "quotationsummary",
      filterData
    );

  const { data: quotationSearch, loading: quotationLoading } =
    PaginationFetchData(
      "quotation",
      filterDataQuote
    );

  const isSearchOrFilterApplied =
    filterDataQuote.SearchTerm ||
    filterDataQuote.CountryName ||
    filterDataQuote.ProjectName ||
    filterDataQuote.DistributorName;

  const quotation = viewType === "quotation"
    ? quotationSearch
    : quotationSummary;


  const loading = viewType === "quotation"
    ? quotationLoading
    : summaryLoading;

  const { data: countryData } = FetchData("country");
  const { data: distributorData } = FetchData("distributor");
  const { data: projectData } = FetchData("project");
  const { data: customerData } = FetchData("Customer");
  const { menuRights } = useRolePermission("Quotation")

  const handleFilterFieldChange = useCallback((rowId, field) => {
    setFilterRows((currentRows) => {
      const isFieldUsedElsewhere = currentRows.some(
        (row) => row.id !== rowId && row.field === field
      );
      if (isFieldUsedElsewhere) return currentRows;
      return currentRows.map((row) =>
        row.id === rowId
          ? { ...row, field, value: null, fromDate: "", toDate: "" }
          : row
      );
    });
  }, []);

  const handleFilterValueChange = useCallback((rowId, value) => {
    setFilterRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId ? { ...row, value } : row
      )
    );
  }, []);

  const handleFilterDateChange = useCallback((rowId, key, value) => {
    setFilterRows((currentRows) =>
      currentRows.map((row) =>
        row.id === rowId ? { ...row, [key]: value } : row
      )
    );
  }, []);

  const addFilterRow = useCallback(() => {
    setFilterRows((currentRows) => {
      const usedFields = currentRows.map((row) => row.field);
      const allFields = ["project", "distributor", "customer", "country", "quoteDate"];
      const nextField = allFields.find((f) => !usedFields.includes(f)) || "project";
      return [...currentRows, createFilterRow(nextField)];
    });
  }, []);

  const removeFilterRow = useCallback((rowId) => {
    setFilterRows((currentRows) => {
      const nextRows = currentRows.filter((row) => row.id !== rowId);
      return nextRows.length > 0 ? nextRows : [createFilterRow()];
    });
  }, []);

  const navigateToQuotationGeneral = useCallback((item) => {
    navigate("/quotation-general", {
      state: {
        row: item,
        id: item?.projectId,
      },
    });
  }, [navigate]);

  const navigateToJobInHand = useCallback((type) => {
    navigate("/job-in-hand", {
      state: {
        quoteType: type,
      },
    });
  }, [navigate]);

  useEffect(() => {
    const totalRecords = quotation?.totalRecords;
    const maxPage = totalRecords ? Math.max(1, Math.ceil(totalRecords / pageSize)) : null;
    const nextPageNumber = maxPage && pageNumber > maxPage ? maxPage : pageNumber;
    const syncPagination = (prev) => {
      if (prev.PageNumber === nextPageNumber && prev.PageSize === pageSize) {
        return prev;
      }

      return {
        ...prev,
        PageNumber: nextPageNumber,
        PageSize: pageSize,
      };
    };

    if (viewType === "quotation") {
      setFilterDataQuote(syncPagination);
    } else {
      setFilterData(syncPagination);
    }
  }, [pageNumber, pageSize, quotation?.totalRecords, viewType]);

  const ClearFilter = useCallback(() => {
    setFilterRows([createFilterRow()]);
    setFilterData((prev) => ({
      ...prev,
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
      PageNumber: 1
    }));
    setFilterDataQuote(prev => ({
      ...prev,
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      SearchTerm: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
      PageNumber: 1
    }));
    setPageNumber(1)
    setHasActiveFilters(false);
  }, []);

  const ApplyFilter = useCallback(() => {

    const nextFilters = {
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
    };

    filterRows.forEach((row) => {
      if (row.field === "project")
        nextFilters.ProjectName = row.value?.label || "";

      if (row.field === "distributor")
        nextFilters.DistributorName = row.value?.label || "";

      if (row.field === "customer")
        nextFilters.CustomerName = row.value?.label || "";

      if (row.field === "country")
        nextFilters.CountryName = row.value?.label || "";

      if (row.field === "quoteDate") {
        nextFilters.QuoteFromDate = row.fromDate || "";
        nextFilters.QuoteToDate = row.toDate || "";
      }
    });


    if (viewType === "quotation") {

      setFilterDataQuote((prev) => ({
        ...prev,
        ...nextFilters,
        SearchTerm: "",
        PageNumber: 1
      }));

    } else {

      setFilterData((prev) => ({
        ...prev,
        ...nextFilters,
        PageNumber: 1
      }));

    }

    setPageNumber(1);
    setSearch("");
    setHasActiveFilters(Object.values(nextFilters).some(Boolean));

  }, [filterRows, viewType]);

  const ApplySearch = useCallback(() => {
    if (viewType === "quotation") {

      setFilterDataQuote(prev => ({
        ...prev,
        SearchTerm: search,
        ProjectName: "",
        CountryName: "",
        DistributorName: "",
        CustomerName: "",
        QuoteFromDate: "",
        QuoteToDate: "",
        PageNumber: 1
      }));

    }
    else {
      setFilterData((prev) => ({
        ...prev,
        ProjectName: search,
        CountryName: "",
        DistributorName: "",
        CustomerName: "",
        QuoteFromDate: "",
        QuoteToDate: "",
        PageNumber: 1
      }));
    }
    setPageNumber(1)
    setFilterRows([createFilterRow()]);
    setHasActiveFilters(false);
  }, [search, viewType]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Enter") {
      ApplySearch();
    }
  }, [ApplySearch]);

  const removeSearch = useCallback(() => {
    setSearch("");
    setFilterDataQuote((prev) => ({
      ...prev,
      SearchTerm: "",
      ProjectName: "",
      CountryName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
      PageNumber: 1
    }));
    setFilterData((prev) => ({
      ...prev,
      ProjectName: "",
      CountryName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
      PageNumber: 1
    }));
    setPageNumber(1)
    setHasActiveFilters(false);
  }, []);

  const RefreshList = useCallback(() => {
    setSearch("");
    setFilterData({
      PageNumber: 1,
      PageSize: 25,
      ProjectName: "",
      CountryName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
    });
    setFilterDataQuote({
      PageNumber: 1,
      PageSize: 25,
      SearchTerm: "",
      userId: userId,
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
    });
    setPageNumber(1)
    setPageSize(25)
    setFilterRows([createFilterRow()]);
    setHasActiveFilters(false);
  }, [userId]);

  const handleViewTypeChange = useCallback((nextViewType) => {
    setViewType(nextViewType);
    setSearch("");
    setPageNumber(1);
    setFilterRows([createFilterRow()]);
    setFilterData((prev) => ({
      ...prev,
      PageNumber: 1,
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
    }));
    setFilterDataQuote((prev) => ({
      ...prev,
      PageNumber: 1,
      SearchTerm: "",
      CountryName: "",
      ProjectName: "",
      DistributorName: "",
      CustomerName: "",
      QuoteFromDate: "",
      QuoteToDate: "",
    }));
    setHasActiveFilters(false);
  }, []);

  return (
    <section id="quotationList">
      <div className="container-fluid">
        <div className="row pt-1">
          <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 p-0">
            <QuotationToolbar
              ApplyFilter={ApplyFilter}
              ApplySearch={ApplySearch}
              ClearFilter={ClearFilter}
              RefreshList={RefreshList}
              countryData={countryData}
              customerData={customerData}
              distributorData={distributorData}
              addFilterRow={addFilterRow}
              filterRows={filterRows}
              handleFilterDateChange={handleFilterDateChange}
              handleFilterFieldChange={handleFilterFieldChange}
              handleFilterValueChange={handleFilterValueChange}
              handleKeyDown={handleKeyDown}
              hasActiveFilters={hasActiveFilters}
              menuRights={menuRights}
              navigateToJobInHand={navigateToJobInHand}
              projectData={projectData}
              removeSearch={removeSearch}
              removeFilterRow={removeFilterRow}
              search={search}
              setSearch={setSearch}
              totalRecords={quotation?.totalRecords}
              viewType={viewType}
              setViewType={handleViewTypeChange}
            />
          </div>
        </div>
        {viewType === "project" ? (
          <QuotationGrid
            loading={summaryLoading}
            navigateToQuotationGeneral={navigateToQuotationGeneral}
            quotation={quotationSummary}
            filterData={filterData}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageSize={pageSize}
            menuRights={menuRights}
          />
        ) : (
        <QuotationGeneralView 
        quotationSearch={quotationSearch}
        loading={quotationLoading}
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        />       
        )}          
      </div>
    </section>
  );
};
export default Quotation;
